import { MutationCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { feelDone, feelRefuse } from './lib/feel.js';
import { rememberInviteFromUrl } from './lib/invite.js';
import { initNativeShell } from './lib/native.js';
import { Suspense, useEffect, useState } from 'react';
import { Redirect, Route, Switch, useLocation } from 'wouter';
import { AppChrome } from './components/chrome/AppChrome.js';
import { ErrorBoundary } from './components/chrome/ErrorBoundary.js';
import { Notifier } from './components/chrome/Notifier.js';
import { SwipeBack } from './components/chrome/SwipeBack.js';
import { useDiscreet } from './lib/discreet.js';
import { useSession } from './lib/session.js';
import { useProfile } from './lib/useProfile.js';
import {
  AjudaPage,
  AppRoutes,
  AtendimentoPage,
  NomePublicoPage,
  VitrinePage,
  VitrineBairroPage,
  ConfirmarVisitaPage,
  ClinicaPublicaPage,
  ConfirmarConsultaPage,
  PagarCobrancaPage,
  FormularioPublicoPage,
  ExcluirContaPage,
  PrivacidadePage,
  TermosPage,
  TermoNegocioPage,
} from './AppRoutes.js';
import { EntrarPage } from './pages/EntrarPage.js';
import { BoasVindasPage } from './pages/BoasVindasPage.js';
import './pages/pages.css';
import { Skeleton } from './components/ui/Skeleton.js';
import { lazy } from 'react';

const TelaPage = lazy(() => import('./pages/TelaPage.js').then((m) => ({ default: m.TelaPage })));
const ResultadoPage = lazy(() =>
  import('./pages/ResultadoPage.js').then((m) => ({ default: m.ResultadoPage })),
);

// Retorno das ações (T7.29): recusa sempre vibra; conclusão importante (meta.feel 'done')
// vibra e, se a pessoa ligou, toca o som de "feito".
const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 1, refetchOnWindowFocus: false } },
  mutationCache: new MutationCache({
    onSuccess: (_data, _variables, _context, mutation) => {
      if (mutation.meta?.feel === 'done') feelDone();
    },
    onError: (_error, _variables, _context, mutation) => {
      if (mutation.meta?.feel !== 'quiet') feelRefuse();
    },
  }),
});

/**
 * Abertura sem o perfil no aparelho: o S respira no lugar em que o rosto vai nascer;
 * só depois de 4 s uma linha curta explica a espera. Com o perfil guardado, a Início
 * desenha direto e o personagem acorda no lugar (ver Palco).
 */
function Splash({ text }: { text: string }) {
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setSlow(true), 4000);
    return () => clearTimeout(timer);
  }, []);
  return (
    <div className="boot" role="status" aria-label={text}>
      <img className="boot-mark" src="/icon-app.png" alt="" />
      <p className={`boot-line${slow ? ' on' : ''}`}>Estou acordando. A rede está lenta.</p>
    </div>
  );
}

/** Enquanto a página chega: o esqueleto dela (só aparece se a espera passar de 300 ms). */
function PageSkeleton() {
  return (
    <div className="pg pg-wait">
      <Skeleton variant="rows" label="Abrindo" />
    </div>
  );
}

function SignedIn() {
  const profileQ = useProfile();
  const [location] = useLocation();
  // Modo discreto da clínica: ligar ou desligar redesenha a tela (sem perder o que está aberto).
  useDiscreet();

  if (profileQ.isLoading) return <Splash text="Abrindo seu Secretário…" />;
  if (profileQ.isError || !profileQ.data) {
    return (
      <div className="pg">
        <h1>Não consegui abrir agora</h1>
        <p className="pg-sub">Confira sua conexão e tente de novo.</p>
        <div className="pg-actions">
          <button className="pg-chip solid" onClick={() => void profileQ.refetch()}>
            Tentar de novo
          </button>
        </div>
      </div>
    );
  }

  // Primeira entrada: a apresentação (oi, primeiro pedido, feito, rosto); a escolha
  // completa do personagem fica em Mudar jeito, como ajuste.
  if (!profileQ.data.onboardedAt) {
    return location === '/boas-vindas' ? (
      <BoasVindasPage />
    ) : (
      <Redirect to="/boas-vindas" replace />
    );
  }

  return (
    <AppChrome>
      <Notifier />
      <SwipeBack />
      <ErrorBoundary where="pagina">
        <Suspense fallback={<PageSkeleton />}>
          <AppRoutes />
        </Suspense>
      </ErrorBoundary>
    </AppChrome>
  );
}

/** Endereço antigo da apresentação: leva para a capa em secretarios.net. */
function ToCapa() {
  useEffect(() => {
    window.location.replace('https://secretarios.net');
  }, []);
  return <Splash text="Abrindo…" />;
}

/** Sem sessão: entrada, mais as páginas públicas que as lojas exigem abertas a qualquer pessoa. */
function SignedOut() {
  return (
    <Suspense fallback={<Splash text="Abrindo…" />}>
      <Switch>
        <Route path="/termos" component={TermosPage} />
        <Route path="/termo-negocio" component={TermoNegocioPage} />
        <Route path="/privacidade" component={PrivacidadePage} />
        <Route path="/excluir-conta" component={ExcluirContaPage} />
        <Route path="/ajuda" component={AjudaPage} />
        <Route path="/entrar" component={EntrarPage} />
        <Route path="/a/:username/confirmar/:token" component={ConfirmarVisitaPage} />
        <Route path="/a/:username" component={AtendimentoPage} />
        <Route path="/loja/:username" component={VitrinePage} />
        <Route path="/v/:placeId" component={VitrineBairroPage} />
        {PagarCobrancaPage && <Route path="/c/:slug/pagar/:token" component={PagarCobrancaPage} />}
        {ConfirmarConsultaPage && (
          <Route path="/c/:slug/confirmar/:token" component={ConfirmarConsultaPage} />
        )}
        {ClinicaPublicaPage && <Route path="/c/:slug" component={ClinicaPublicaPage} />}
        {FormularioPublicoPage && <Route path="/f/:token" component={FormularioPublicoPage} />}
        {/* A apresentação mora em secretarios.net; aqui, quem chega sem conta já entra no cadastro. */}
        <Route path="/conhecer" component={ToCapa} />
        <Route path="/" component={EntrarPage} />
        {/* secretarios.net/<nome>: a página do negócio pelo nome curto; sem dono, a entrada. */}
        <Route path="/:nome">{() => <NomePublicoPage fallback={<EntrarPage />} />}</Route>
        <Route component={EntrarPage} />
      </Switch>
    </Suspense>
  );
}

function Gate() {
  const session = useSession();
  const [location] = useLocation();
  // Resultado mandado por link: abre para quem tem o link, com ou sem conta.
  if (location.startsWith('/r/')) {
    return (
      <Suspense fallback={<Splash text="Abrindo…" />}>
        <ResultadoPage />
      </Suspense>
    );
  }
  // A tela grande (TV, notebook) abre sem conta: ela se pareia por código.
  if (location === '/tela') {
    return (
      <Suspense fallback={<Splash text="Abrindo…" />}>
        <TelaPage />
      </Suspense>
    );
  }
  return session ? <SignedIn /> : <SignedOut />;
}

export function App() {
  useEffect(() => {
    void initNativeShell();
    rememberInviteFromUrl();
  }, []);
  return (
    <QueryClientProvider client={queryClient}>
      {/* Última rede: erro fora das páginas (barra, palco, camadas) também vira tela com saída. */}
      <ErrorBoundary where="raiz">
        <Gate />
      </ErrorBoundary>
    </QueryClientProvider>
  );
}
